/**
 * Room to move, judged at the end of a look-ahead: surviving six scans is no use if Digby ends up
 * walled in, cornered in a dead end with a creature coming, or running down a shaft ahead of the
 * boulder that will land on him at the bottom.
 */

import { E } from '../sim';
import type { CaveState } from '../sim';
import { isCreature } from './danger';

const { SPACE, DIRT, STEEL, DIAMOND, CLOCK, KEY, OUTBOX_OPEN, BOULDER, BOULDER_FALLING, DIAMOND_FALLING, DIGBY } = E;
const creature = isCreature;

/**
 * Digby must be able to get to at least this many cells, each at least two scans before any
 * creature could: not walled in, and not cornered in a dead end with a creature coming.
 */
const FREEDOM = 6;
/**
 * With a creature a few moves from Digby he may have to keep moving, so he needs more room: a
 * short dead end (a vault, a pocket among boulders) is a trap even when he gets there first. The
 * room needed grows from `FREEDOM` to `FREEDOM_CHASED` as the nearest creature comes within
 * `WARY_MOVES` of him.
 */
const FREEDOM_CHASED = 16;
const WARY_MOVES = 20;
/** How far (in moves) creatures are followed for that race. */
const RACE = 24;

/** Digby is alive but something will kill him next scan whatever he does. */
export function imperiled(t: CaveState): boolean {
  const { x, y } = t.digby;
  const { w, h, cells } = t;
  if (y > 0) {
    const above = cells[(y - 1) * w + x] ?? STEEL;
    if (above === BOULDER_FALLING || above === DIAMOND_FALLING) return true;
  }
  if (x > 0 && creature(cells[y * w + x - 1] ?? STEEL)) return true;
  if (x < w - 1 && creature(cells[y * w + x + 1] ?? STEEL)) return true;
  if (y > 0 && creature(cells[(y - 1) * w + x] ?? STEEL)) return true;
  return y < h - 1 && creature(cells[(y + 1) * w + x] ?? STEEL);
}

function open(el: number): boolean {
  return el === SPACE || el === DIRT || el === DIAMOND || el === OUTBOX_OPEN || el === CLOCK || (el >= KEY && el < KEY + 3);
}

export class Room {
  private readonly seen: Int32Array;
  private readonly queue: Int32Array;
  /** Moves for the nearest creature to reach each cell, valid where `chased` holds the mark. */
  private readonly chase: Int32Array;
  private readonly chased: Int32Array;
  private readonly dist: Int32Array;
  private mark = 0;

  constructor(n: number) {
    this.seen = new Int32Array(n);
    this.queue = new Int32Array(n);
    this.chase = new Int32Array(n);
    this.chased = new Int32Array(n);
    this.dist = new Int32Array(n);
  }

  /** Creatures spread through space one cell per scan: how soon can one be at each cell? */
  private creatures(t: CaveState, mark: number): void {
    const { w, h, cells } = t;
    const { chase, chased, queue } = this;
    let head = 0;
    let tail = 0;
    for (let c = 0; c < cells.length; c++) {
      if (!creature(cells[c] ?? STEEL)) continue;
      chased[c] = mark;
      chase[c] = 0;
      queue[tail++] = c;
    }
    while (head < tail) {
      const c = queue[head++] ?? 0;
      const d = chase[c] ?? 0;
      if (d >= RACE) continue;
      const x = c % w;
      for (let k = 0; k < 4; k++) {
        if ((k === 1 && x === w - 1) || (k === 3 && x === 0)) continue;
        const nb = k === 0 ? c - w : k === 1 ? c + 1 : k === 2 ? c + w : c - 1;
        if (nb < 0 || nb >= w * h || chased[nb] === mark) continue;
        const el = cells[nb] ?? STEEL;
        // Digby's own cell counts as space: it is how near a creature is to him.
        const clear = el === SPACE || el === DIGBY;
        if (!clear && el !== DIRT) continue;
        chased[nb] = mark;
        // Dirt only opens up once Digby digs it, so a creature gets there later if at all.
        chase[nb] = d + (clear ? 1 : RACE);
        if (clear) queue[tail++] = nb;
      }
    }
  }

  /**
   * Space that something falling or unsupported is about to drop through does not count as room -
   * nor does the space below Digby when it is his column: whatever falls into the cell he leaves
   * keeps falling after him.
   */
  private falls(t: CaveState, mark: number): void {
    const { w, cells } = t;
    const n = cells.length;
    for (let c = 0; c + w < n; c++) {
      const el = cells[c];
      const loose = el === BOULDER_FALLING || el === DIAMOND_FALLING;
      const below = cells[c + w];
      if (!loose && !((el === BOULDER || el === DIAMOND) && (below === SPACE || below === DIGBY))) continue;
      for (let j = c + w; j < n && (cells[j] === SPACE || cells[j] === DIGBY); j += w) this.seen[j] = mark;
    }
  }

  /** Can Digby still get to `FREEDOM` cells (or the open exit), each well ahead of any creature? */
  free(t: CaveState): boolean {
    const { w, h, cells } = t;
    const { seen, queue, chase, chased, dist } = this;
    const mark = ++this.mark;
    this.creatures(t, mark);
    this.falls(t, mark);
    const start = t.digby.y * w + t.digby.x;
    const near = chased[start] === mark ? (chase[start] ?? RACE) : RACE;
    const needed = Math.min(FREEDOM_CHASED, Math.max(FREEDOM, WARY_MOVES - near));
    let head = 0;
    let tail = 0;
    queue[tail++] = start;
    seen[start] = mark;
    dist[start] = 0;
    let count = 1;
    while (head < tail) {
      if (count >= needed) return true;
      const c = queue[head++] ?? 0;
      const d = (dist[c] ?? 0) + 1;
      const x = c % w;
      for (let k = 0; k < 4; k++) {
        if ((k === 1 && x === w - 1) || (k === 3 && x === 0)) continue;
        const nb = k === 0 ? c - w : k === 1 ? c + 1 : k === 2 ? c + w : c - 1;
        if (nb < 0 || nb >= w * h || seen[nb] === mark || !open(cells[nb] ?? STEEL)) continue;
        seen[nb] = mark;
        // Beside a creature is as bad as on it: Digby must get there two scans ahead.
        if (chased[nb] === mark && (chase[nb] ?? 0) <= d + 1) continue;
        // A way out of the cave is all the room he needs.
        if (cells[nb] === OUTBOX_OPEN) return true;
        dist[nb] = d;
        queue[tail++] = nb;
        count++;
      }
    }
    return count >= needed;
  }
}
